import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { ToolHubInputSchema, type ToolHub } from "../../shared/tool-hubs";
import { Button } from "../components/ui/button";
import { Field, FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { SheetBody, SheetFooter, SheetForm } from "../components/ui/sheet";
import { Textarea } from "../components/ui/textarea";
import { saveToolHub } from "./tool-hubs-api";

type HubDraft = { name: string; summary: string; kickoffOn: string; goLiveOn: string };
const draftOf = (hub?: ToolHub): HubDraft => ({
  name: hub?.name ?? "",
  summary: hub?.summary ?? "",
  kickoffOn: hub?.kickoffOn ?? "",
  goLiveOn: hub?.goLiveOn ?? "",
});

/** Creates a hub when `hub` is absent; otherwise edits it. */
export function HubEditForm({
  organizationId,
  hub,
  onDone,
  onCancel,
}: {
  organizationId: string;
  hub?: ToolHub;
  onDone: (saved: ToolHub) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(() => draftOf(hub));
  const save = useMutation({
    mutationFn: () =>
      saveToolHub(
        organizationId,
        hub?.id ?? null,
        ToolHubInputSchema.parse({
          name: draft.name,
          summary: draft.summary,
          kickoffOn: draft.kickoffOn || null,
          goLiveOn: draft.goLiveOn || null,
          toolInstanceId: hub?.toolInstanceId ?? null,
        }),
      ),
    onSuccess: (result) => {
      onDone(result.hub);
    },
  });
  return (
    <SheetForm
      aria-label={hub ? "Edit tool" : "New tool"}
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <SheetBody>
        <fieldset disabled={save.isPending} className="space-y-4">
          <HubFields
            draft={draft}
            onChange={(key, value) => {
              setDraft((current) => ({ ...current, [key]: value }));
            }}
          />
        </fieldset>
        {save.error ? <FieldError>{save.error.message}</FieldError> : null}
      </SheetBody>
      <SheetFooter>
        <Button type="button" variant="ghost" disabled={save.isPending} onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={save.isPending || !draft.name.trim()}>
          {save.isPending ? "Saving…" : hub ? "Save" : "Create tool"}
        </Button>
      </SheetFooter>
    </SheetForm>
  );
}

function HubFields({
  draft,
  onChange,
}: {
  draft: HubDraft;
  onChange: (key: keyof HubDraft, value: string) => void;
}) {
  return (
    <>
      <Field>
        <FieldLabel htmlFor="hub-name">Tool or project</FieldLabel>
        <Input
          id="hub-name"
          value={draft.name}
          maxLength={160}
          placeholder="monday.com integration"
          onChange={(event) => {
            onChange("name", event.target.value);
          }}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="hub-summary">What it is (optional)</FieldLabel>
        <Textarea
          id="hub-summary"
          rows={2}
          maxLength={2000}
          value={draft.summary}
          onChange={(event) => {
            onChange("summary", event.target.value);
          }}
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field>
          <FieldLabel htmlFor="hub-kickoff">Kickoff</FieldLabel>
          <Input
            id="hub-kickoff"
            type="date"
            value={draft.kickoffOn}
            onChange={(event) => {
              onChange("kickoffOn", event.target.value);
            }}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="hub-go-live">Go-live</FieldLabel>
          <Input
            id="hub-go-live"
            type="date"
            value={draft.goLiveOn}
            onChange={(event) => {
              onChange("goLiveOn", event.target.value);
            }}
          />
        </Field>
      </div>
    </>
  );
}
